import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
  Skeleton,
  Chip,
  alpha,
  styled,
  Modal,
  Backdrop,
  Fade,
  Checkbox,
  IconButton,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { apiErrorMessage, appointmentsService, shopsService } from '../../services/api';
import {
  formatClockTime,
  shopToday,
  shopWallClockToEpoch,
  shopZoneAbbreviation,
  toDateKey,
} from '../../utils/shopTime';
import { findNextOpenDate, isDateOpen } from '../../utils/shopAvailability';
import { CreateAppointmentDto, Shop } from '../../types';
import { useShop } from '../../contexts/ShopContext';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import OilBarrelIcon from '@mui/icons-material/LocalGasStation';
import TireRepairIcon from '@mui/icons-material/DirectionsCar';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import { motion } from 'motion/react';

// Styled components for the themed UI
const GradientBackground = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #1a1b1f 0%, #2d2e35 100%)',
  position: 'relative',
  overflow: 'hidden',
  color: '#fff',
  minHeight: '100vh',
}));

const GradientOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at 50% 30%, rgba(82, 109, 254, 0.15) 0%, transparent 70%)',
  zIndex: 0,
}));

const BackgroundLines = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
  backgroundPosition: 'center center',
  zIndex: 0,
  opacity: 0.5,
}));

// Styled paper with glassmorphism effect
const GlassPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(8px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

// Section heading
const SectionHeading = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: -8,
    height: 4,
    width: 60,
    background: 'linear-gradient(90deg, #3a86ff 0%, #4361ee 100%)',
    boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
    borderRadius: 4,
  }
}));

// Gradient button
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #3a86ff 0%, #4361ee 100%)',
  color: '#fff',
  fontWeight: 600,
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(58, 134, 255, 0.3), 0 0 15px rgba(255, 215, 0, 0.6)',
  border: '2px solid rgba(255, 223, 0, 0.7)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1, 
    bottom: -1,
    borderRadius: '10px',
    background: 'linear-gradient(45deg, #FFD700, #FFA500)',
    opacity: 0.4,
    filter: 'blur(3px)',
    zIndex: -1
  },
  '&:hover': {
    boxShadow: '0 8px 25px rgba(58, 134, 255, 0.5), 0 0 25px rgba(255, 215, 0, 0.7)',
    background: 'linear-gradient(90deg, #4361ee 0%, #3a86ff 100%)',
    border: '2px solid rgba(255, 223, 0, 0.9)',
    transform: 'translateY(-2px)',
    transition: 'all 0.3s ease'
  }
}));

// Styled text field for dark theme
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.07)',
    borderRadius: '12px',
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3a86ff',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputAdornment-root': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
  '& .MuiFormHelperText-root': {
    color: alpha('#fff', 0.7),
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#ff6b6b',
  },
}));

// Styled select for dark theme
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.07)',
    borderRadius: '12px',
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3a86ff',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiFormHelperText-root': {
    color: alpha('#fff', 0.7),
  },
}));

// Styled chip for time selection
const TimeChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  background: 'rgba(255, 255, 255, 0.07)',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&.MuiChip-colorPrimary': {
    background: 'linear-gradient(90deg, #3a86ff 0%, #4361ee 100%)',
    fontWeight: 'bold',
    boxShadow: '0 0 10px rgba(58, 134, 255, 0.3)',
  },
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
  },
  '&.Mui-disabled': {
    opacity: 0.8,
    color: 'rgba(180, 180, 180, 0.8)',
    background: 'rgba(70, 70, 70, 0.3)',
    border: '1px solid rgba(120, 120, 120, 0.4)',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '1px',
      backgroundColor: 'rgba(200, 200, 200, 0.8)',
      left: 0,
      top: '50%',
    }
  },
}));

// Styled alert for dark theme
const StyledAlert = styled(Alert)(({ theme, severity }) => ({
  background: severity === 'error' 
    ? 'rgba(255, 50, 50, 0.1)' 
    : severity === 'warning' 
      ? 'rgba(255, 155, 0, 0.1)' 
      : severity === 'info' 
        ? 'rgba(58, 134, 255, 0.1)' 
        : 'rgba(50, 205, 50, 0.1)',
  color: '#fff',
  border: `1px solid ${
    severity === 'error' 
      ? 'rgba(255, 50, 50, 0.2)' 
      : severity === 'warning' 
        ? 'rgba(255, 155, 0, 0.2)' 
        : severity === 'info' 
          ? 'rgba(58, 134, 255, 0.2)' 
          : 'rgba(50, 205, 50, 0.2)'
  }`,
}));

const ValidationSchema = Yup.object().shape({
  customerName: Yup.string().required('Name is required'),
  customerEmail: Yup.string().email('Invalid email').required('Email is required'),
  customerPhone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  vehicleModel: Yup.string()
    .min(2, 'Vehicle model must be at least 2 characters')
    .required('Vehicle model is required'),
  vehicleYear: Yup.string().required('Vehicle year is required'),
  // Mirrors the API's rule (license-plate.ts): optional, up to 12 characters.
  licensePlate: Yup.string()
    .trim()
    .max(12, 'A plate is at most 12 characters')
    .matches(/^[A-Za-z0-9 -]*$/, 'Letters, numbers, spaces and hyphens only')
    .optional(),
  serviceDescription: Yup.string()
    .min(10, 'Please provide more details about the service needed')
    .required('Service description is required'),
  comments: Yup.string().optional(),
  appointmentDate: Yup.date().required('Date is required'),
  appointmentTime: Yup.string().required('Time is required'),
  shopId: Yup.string().required('Shop selection is required'),
});

/**
 * Form state, not the request payload. The date picker holds a `Date` and the
 * slot list an "HH:mm" string; both are folded into a single epoch
 * `appointmentDateTime` on submit, which is all the API accepts.
 */
type BookingFormValues = Omit<CreateAppointmentDto, 'appointmentDateTime'> & {
  appointmentDate: Date | null;
  appointmentTime: string;
};

const initialValues: BookingFormValues = {
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  vehicleModel: '',
  vehicleYear: '',
  licensePlate: '',
  serviceDescription: '',
  comments: '',
  appointmentDate: null,
  appointmentTime: '',
  shopId: '',
};

const vehicleYears = Array.from({ length: 50 }, (_, i) => (new Date().getFullYear() - i).toString());

// Update the time slot generation function to use the shop's time slot settings
// Service selection data
const AUTOMOTIVE_SERVICES = [
  {
    id: 'brakes',
    icon: BuildIcon,
    title: 'Brakes',
    description: 'Issues, pads, rotors, inspection',
    category: 'popular'
  },
  {
    id: 'oil-change',
    icon: OilBarrelIcon,
    title: 'Oil Change',
    description: 'Oil change, filters, lube',
    category: 'popular'
  },
  {
    id: 'inspection',
    icon: CarRepairIcon,
    title: 'Vehicle Inspection',
    description: 'State, emissions, safety',
    category: 'popular'
  },
  {
    id: 'tires',
    icon: TireRepairIcon,
    title: 'Tires',
    description: 'Replacement, rotations, alignments',
    category: 'popular'
  },
  {
    id: 'battery',
    icon: BatteryChargingFullIcon,
    title: 'Battery',
    description: 'Battery replacement, testing, starter',
    category: 'popular'
  },
  {
    id: 'engine-transmission',
    icon: SettingsIcon,
    title: 'Engine & Transmission',
    description: 'Issue diagnosis, fluids, drivetrain',
    category: 'popular'
  },
  {
    id: 'hvac',
    icon: AcUnitIcon,
    title: 'Heat or A/C',
    description: 'No heat, no AC, intermittent issues',
    category: 'popular'
  },
  {
    id: 'maintenance',
    icon: ScheduleIcon,
    title: 'Scheduled Maintenance',
    description: 'Services recommended at specific mileage',
    category: 'popular'
  },
  {
    id: 'custom-issue',
    icon: DescriptionIcon,
    title: 'Describe Your Issue',
    description: 'Tell us what\'s happening',
    category: 'custom'
  },
  {
    id: 'specific-part',
    icon: SettingsIcon,
    title: 'Select Specific Part',
    description: 'Choose a specific component',
    category: 'custom'
  }
];

// Styled modal
const ServiceModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1b1f 0%, #2d2e35 100%)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  padding: theme.spacing(4),
  maxWidth: '800px',
  maxHeight: '90vh',
  overflow: 'auto',
  color: '#fff',
  position: 'relative',
}));

const ServiceCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '12px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  padding: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(58, 134, 255, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&.selected': {
    background: 'linear-gradient(90deg, rgba(58, 134, 255, 0.2) 0%, rgba(67, 97, 238, 0.2) 100%)',
    borderColor: '#3a86ff',
    boxShadow: '0 0 20px rgba(58, 134, 255, 0.3)',
  }
}));


const BookAppointmentPage: React.FC = () => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState<boolean>(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedShopId, setSelectedShopId] = useState<string>('');
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState<boolean>(false);
  const { selectedShop } = useShop();
  const navigate = useNavigate();

  // Add state for shop settings
  const [shopSettings, setShopSettings] = useState<any>(null);

  // The shop's clock, from the public shop read. Anonymous visitors cannot read
  // shop settings, and without a zone every date and time on this page — the
  // booked instant included — silently used the visitor's own laptop clock.
  const shopTimeZone: string | undefined =
    (selectedShop && selectedShop.id === selectedShopId ? selectedShop.timezone : undefined) ??
    shops.find((shop) => shop.id === selectedShopId)?.timezone ??
    shopSettings?.timezone;
  // Set when the slots request itself failed, as opposed to the shop simply
  // being closed — the two need different messages.
  const [slotsFailed, setSlotsFailed] = useState(false);
  
  // Service selection modal state
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [customIssueText, setCustomIssueText] = useState('');
  const [selectedPart, setSelectedPart] = useState('');
  const [currentSetFieldValue, setCurrentSetFieldValue] = useState<any>(null);

  // Add functions to check if a date is available
  // Thin bindings over the shared, pure helpers in utils/shopAvailability.
  // Anything that needs to test a date against *freshly fetched* settings must
  // call isDateOpen/findNextOpenDate directly instead — see the effect below.
  const isDateAvailable = useCallback(
    (date: Date): boolean => isDateOpen(date, shopSettings),
    [shopSettings]
  );

  // Set initial values based on shop from URL/context
  const getInitialValues = () => {
    const values = { ...initialValues };
    if (selectedShop) {
      values.shopId = selectedShop.id;
    }
    return values;
  };

  // Set selected shop from context if available
  useEffect(() => {
    if (selectedShop) {
      setSelectedShopId(selectedShop.id);
    }
  }, [selectedShop]);

  // Load shops for dropdown (only if no shop in context)
  useEffect(() => {
    if (selectedShop) return; // Skip loading if we have a shop from context
    
    const fetchShops = async () => {
      try {
        setIsLoadingShops(true);
        const shopsData = await shopsService.getAllShops();
        setShops(shopsData.filter(shop => shop.isActive));
      } catch (error) {
        console.error('Failed to load shops:', error);
        setErrorMessage('Failed to load available shops. Please try again later.');
      } finally {
        setIsLoadingShops(false);
      }
    };

    fetchShops();
  }, [selectedShop]);

  // Load shop settings when a shop is selected
  useEffect(() => {
    const fetchShopSettings = async () => {
      if (!selectedShopId) return;
      
      try {
        const settings = await shopsService.getShopSettings(selectedShopId);
        setShopSettings(settings);
        
        // Test against `settings`, not the state above: setShopSettings does not
        // take effect until the next render, so reading state here saw `null`
        // and treated every day as open — which is how the picker used to open
        // on a day the shop is closed.
        const today = shopToday(shopTimeZone);
        setSelectedDate(findNextOpenDate(today, settings));
        
        // Slots are owned by the effect below, which asks the API rather than
        // generating them locally.
      } catch (error) {
        // Expected for anonymous visitors: this route is admin/mechanic-only.
        // Settings are only used here for date-picker niceties, so carry on —
        // the slots endpoint applies the real business hours either way.
        console.warn('Shop settings unavailable; falling back to open dates.', error);
        setSelectedDate(shopToday(shopTimeZone));
      }
    };
    
    fetchShopSettings();
  }, [selectedShopId, shopTimeZone]);

  /**
   * The public `available-slots` endpoint is the single source of truth: it
   * applies the shop's business hours, special hours, holidays and slot size in
   * the shop's own timezone, and already drops any slot that has reached
   * `maxConcurrentBookings`.
   *
   * This used to also call `GET /appointments/shop/:shopId` to count bookings
   * client-side, but that route is admin/mechanic-only — it 401s for the
   * customers this page exists for, which is what produced the "Failed to load
   * available time slots" banner and the generic 30-minute fallback slots.
   * Making it public was never an option either: it returns every customer's
   * name, phone and email.
   */
  useEffect(() => {
    const fetchAvailableTimeSlots = async () => {
      if (!selectedShopId || !selectedDate) {
        setAvailableTimeSlots([]);
        return;
      }

      try {
        setIsLoadingTimeSlots(true);
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');
        const slots = await appointmentsService.getAvailableTimeSlots(
          selectedShopId,
          formattedDate,
        );

        // An empty list is a real answer — holiday, closed day, or fully
        // booked. Generating local slots to fill the gap is what made the page
        // offer times the shop does not actually open for.
        setAvailableTimeSlots(slots.map((slot) => slot.substring(0, 5)));
        setSlotsFailed(false);
      } catch (error) {
        console.error('Failed to load available time slots:', error);
        setAvailableTimeSlots([]);
        setSlotsFailed(true);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    };

    fetchAvailableTimeSlots();
  }, [selectedShopId, selectedDate]);

  // Add a custom date filter function for the date picker
  const shouldDisableDate = (date: Date): boolean => {
    return !isDateAvailable(date);
  };

  // Service selection functions
  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const buildServiceDescription = () => {
    const services = selectedServices
      .filter(id => id !== 'custom-issue' && id !== 'specific-part')
      .map(id => AUTOMOTIVE_SERVICES.find(s => s.id === id)?.title)
      .filter(Boolean);
    
    let description = services.join(', ');
    
    if (selectedServices.includes('custom-issue') && customIssueText) {
      description += (description ? '; ' : '') + `Issue: ${customIssueText}`;
    }
    
    if (selectedServices.includes('specific-part') && selectedPart) {
      description += (description ? '; ' : '') + `Part: ${selectedPart}`;
    }
    
    return description || 'Service needed';
  };

  const handleServiceModalClose = () => {
    const description = buildServiceDescription();
    if (currentSetFieldValue) {
      currentSetFieldValue('serviceDescription', description);
    }
    setServiceModalOpen(false);
  };

  const handleSubmit = async (values: typeof initialValues, { resetForm }: { resetForm: () => void }) => {
    if (!values.appointmentDate) {
      setErrorMessage('Please select an appointment date.');
      return;
    }

    try {
      setErrorMessage(null);
      
      // Format phone number to E.164 format (add +1 for US numbers)
      const formattedPhone = values.customerPhone.startsWith('+') 
        ? values.customerPhone 
        : `+1${values.customerPhone}`;
      
      const formattedTime = values.appointmentTime; // "HH:mm", as the slot list gives it

      // The slot is a time in the *shop's* zone. Reading the picker's date with
      // toISOString() shifts it a day for anyone west of UTC, and building the
      // instant with `new Date(...)` reads it in the browser's zone — so a
      // customer booking from another state used to file the wrong hour.
      const appointmentDateTime = shopWallClockToEpoch(
        toDateKey(values.appointmentDate),
        formattedTime,
        shopTimeZone
      );
      const bookedFor = `${format(values.appointmentDate, 'EEEE, MMMM d')} at ${formatClockTime(
        formattedTime
      )} ${shopZoneAbbreviation(shopTimeZone)}`.trim();

      // appointmentDate/appointmentTime are form state (a Date and an "HH:mm"),
      // not part of the payload — the server derives its own mirrors from the
      // instant below.
      const {
        appointmentDate: _formDate,
        appointmentTime: _formTime,
        ...customerValues
      } = values;

      const appointmentData: CreateAppointmentDto = {
        ...customerValues,
        appointmentDateTime,
        customerPhone: formattedPhone, // Use formatted phone number
      };

      console.log('Submitting appointment data:', appointmentData);

      // Submit appointment
      const response = await appointmentsService.createAppointment(appointmentData);
      console.log('Appointment created successfully:', response);
      
      // Reset form and show success message
      resetForm();
      setSelectedDate(null);
      setSuccessMessage(`Booked for ${bookedFor}. We will contact you to confirm.`);
      
      // Navigate to home page after short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error: any) {
      console.error('Failed to book appointment:', error);
      console.error('Request data:', error.config?.data);
      console.error('Response data:', error.response?.data);
      setErrorMessage(
        apiErrorMessage(error, 'Failed to book appointment. Please try again.')
      );
    }
  };


  return (
    <GradientBackground>
      <GradientOverlay />
      <BackgroundLines />
      
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <GlassPaper sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3, color: '#fff' }}>
              Book Your Appointment
            </Typography>
            
            {/* Display selected shop prominently */}
            {selectedShop && (
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Chip
                  icon={<StorefrontIcon />}
                  label={`Booking at: ${selectedShop.name}`}
                  color="primary"
                  variant="filled"
                  sx={{ 
                    fontSize: '1rem', 
                    p: 1, 
                    background: 'linear-gradient(90deg, #3a86ff 0%, #4361ee 100%)',
                    boxShadow: '0 0 10px rgba(58, 134, 255, 0.3)',
                  }}
                />
              </Box>
            )}

            {errorMessage && (
              <StyledAlert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </StyledAlert>
            )}

            <Formik
              initialValues={getInitialValues()}
              validationSchema={ValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                <Form>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={3}>
                      {/* Shop selection - only show if no shop in context */}
                      {!selectedShop && (
                        <Grid size={{ xs: 12 }}>
                          <StyledFormControl fullWidth error={touched.shopId && Boolean(errors.shopId)}>
                            <InputLabel id="shop-select-label">Select Shop *</InputLabel>
                            <Select
                              labelId="shop-select-label"
                              id="shopId"
                              name="shopId"
                              value={values.shopId}
                              label="Select Shop *"
                              onChange={(e) => {
                                handleChange(e);
                                setSelectedShopId(e.target.value);
                              }}
                              onBlur={handleBlur}
                              disabled={isLoadingShops || shops.length === 0}
                            >
                              {shops.map((shop) => (
                                <MenuItem key={shop.id} value={shop.id}>
                                  {shop.name} - {shop.address}
                                </MenuItem>
                              ))}
                            </Select>
                            {touched.shopId && errors.shopId && (
                              <Typography color="error" variant="caption" sx={{ color: '#ff6b6b' }}>
                                {errors.shopId}
                              </Typography>
                            )}
                          </StyledFormControl>
                        </Grid>
                      )}

                      {/* Customer Info */}
                      <Grid size={{ xs: 12 }}>
                        <SectionHeading variant="h6" sx={{ color: '#fff', mt: 2 }}>
                          Your Information
                        </SectionHeading>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <StyledTextField 
                          fullWidth 
                          required 
                          id="customerName" 
                          name="customerName" 
                          label="Full Name" 
                          value={values.customerName} 
                          onChange={handleChange} 
                          onBlur={handleBlur} 
                          error={touched.customerName && Boolean(errors.customerName)} 
                          helperText={touched.customerName && errors.customerName} 
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <StyledTextField 
                          fullWidth 
                          required 
                          id="customerEmail" 
                          name="customerEmail" 
                          label="Email Address" 
                          type="email" 
                          value={values.customerEmail} 
                          onChange={handleChange} 
                          onBlur={handleBlur} 
                          error={touched.customerEmail && Boolean(errors.customerEmail)} 
                          helperText={touched.customerEmail && errors.customerEmail} 
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <StyledTextField 
                          fullWidth 
                          required 
                          id="customerPhone" 
                          name="customerPhone" 
                          label="Phone Number" 
                          placeholder="Enter 10-digit phone number"
                          value={values.customerPhone} 
                          onChange={handleChange} 
                          onBlur={handleBlur} 
                          error={touched.customerPhone && Boolean(errors.customerPhone)} 
                          helperText={touched.customerPhone && errors.customerPhone} 
                        />
                      </Grid>

                      {/* Vehicle Info */}
                      <Grid size={{ xs: 12 }}>
                        <SectionHeading variant="h6" sx={{ color: '#fff', mt: 2 }}>
                          Vehicle Information
                        </SectionHeading>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <StyledTextField 
                          fullWidth 
                          required 
                          id="vehicleModel" 
                          name="vehicleModel" 
                          label="Make & Model" 
                          placeholder="e.g. Toyota Camry" 
                          value={values.vehicleModel} 
                          onChange={handleChange} 
                          onBlur={handleBlur} 
                          error={touched.vehicleModel && Boolean(errors.vehicleModel)} 
                          helperText={touched.vehicleModel && errors.vehicleModel} 
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <StyledFormControl fullWidth error={touched.vehicleYear && Boolean(errors.vehicleYear)}>
                          <InputLabel id="vehicle-year-label">Year *</InputLabel>
                          <Select
                            labelId="vehicle-year-label"
                            id="vehicleYear"
                            name="vehicleYear"
                            value={values.vehicleYear}
                            label="Year *"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {vehicleYears.map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.vehicleYear && errors.vehicleYear && (
                            <Typography color="error" variant="caption" sx={{ color: '#ff6b6b' }}>
                              {errors.vehicleYear}
                            </Typography>
                          )}
                        </StyledFormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <StyledTextField
                          fullWidth
                          id="licensePlate"
                          name="licensePlate"
                          label="License plate (optional)"
                          placeholder="e.g. 8KDL 204"
                          value={values.licensePlate ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.licensePlate && Boolean(errors.licensePlate)}
                          helperText={
                            (touched.licensePlate && errors.licensePlate) ||
                            'Helps the shop find your car on the lot'
                          }
                          slotProps={{ htmlInput: { maxLength: 12, autoCapitalize: 'characters' } }}
                        />
                      </Grid>

                      {/* Service Info */}
                      <Grid size={{ xs: 12 }}>
                        <SectionHeading variant="h6" sx={{ color: '#fff', mt: 2 }}>
                          Service Details
                        </SectionHeading>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Field name="appointmentDate">
                          {({ field, form }: FieldProps) => (
                            <DatePicker
                              label="Appointment Date *"
                              value={values.appointmentDate}
                              onChange={(newDate) => {
                                setFieldValue('appointmentDate', newDate);
                                setSelectedDate(newDate);
                                setFieldValue('appointmentTime', '');
                              }}
                              // The shop's today, not the visitor's.
                              minDate={shopToday(shopTimeZone)}
                              shouldDisableDate={shouldDisableDate}
                              slotProps={{ 
                                textField: { 
                                  fullWidth: true, 
                                  required: true, 
                                  error: touched.appointmentDate && Boolean(errors.appointmentDate), 
                                  helperText: touched.appointmentDate ? errors.appointmentDate : '',
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      background: 'rgba(255, 255, 255, 0.07)',
                                      color: '#fff',
                                      '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.2)',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#3a86ff',
                                      },
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: 'rgba(255, 255, 255, 0.7)',
                                    },
                                  }
                                } 
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}></Grid> {/* Spacer */} 

                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, color: '#fff' }}>
                          Select Available Time: *
                        </Typography>
                        {isLoadingTimeSlots ? (
                          <Box sx={{ mt: 1 }}>
                            <Skeleton variant="rectangular" height={60} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                          </Box>
                        ) : !values.appointmentDate || !values.shopId ? (
                          <StyledAlert severity="info" sx={{ mt: 1 }}>
                            Select shop and date first.
                          </StyledAlert>
                        ) : (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {slotsFailed ? (
                              <StyledAlert severity="error">
                                Couldn't load times for this date. Please try again.
                              </StyledAlert>
                            ) : availableTimeSlots.length === 0 ? (
                              <StyledAlert severity="warning">
                                No slots available. Try another date.
                              </StyledAlert>
                            ) : (
                              availableTimeSlots.map((time) => {
                                return (
                                  <TimeChip
                                    key={time}
                                    label={formatClockTime(time)}
                                    onClick={() => setFieldValue('appointmentTime', time)}
                                    color={values.appointmentTime === time ? 'primary' : 'default'}
                                    variant={values.appointmentTime === time ? 'filled' : 'outlined'}
                                  />
                                );
                              })
                            )}
                          </Box>
                        )}
                        {shopTimeZone && values.appointmentDate && values.shopId && !slotsFailed && availableTimeSlots.length > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                            Times are the shop's local time ({shopZoneAbbreviation(shopTimeZone)}).
                          </Typography>
                        )}
                        {touched.appointmentTime && errors.appointmentTime && (
                          <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, color: '#ff6b6b' }}>
                            {errors.appointmentTime}
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Box>
                          <Typography variant="subtitle1" gutterBottom sx={{ color: '#fff' }}>
                            Reason for Visit / Service Needed *
                          </Typography>
                          <Box
                            onClick={() => {
                              setServiceModalOpen(true);
                              setCurrentSetFieldValue(() => setFieldValue);
                            }}
                            sx={{
                              background: 'rgba(255, 255, 255, 0.07)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              padding: 2,
                              minHeight: '120px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                background: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {values.serviceDescription ? (
                              <Typography sx={{ color: '#fff' }}>
                                {values.serviceDescription}
                              </Typography>
                            ) : (
                              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                Click to select services and describe what you need
                              </Typography>
                            )}
                          </Box>
                          {touched.serviceDescription && errors.serviceDescription && (
                            <Typography color="error" variant="caption" sx={{ color: '#ff6b6b', mt: 1, display: 'block' }}>
                              {errors.serviceDescription}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <StyledTextField
                          fullWidth
                          id="comments"
                          name="comments"
                          label="Additional Comments (Optional)"
                          placeholder="Any additional information or special requests..."
                          multiline
                          rows={3}
                          value={values.comments}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.comments && Boolean(errors.comments)}
                          helperText={touched.comments && errors.comments}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                  
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <GradientButton 
                      type="submit" 
                      size="large" 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Confirm Appointment'}
                    </GradientButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </GlassPaper>
          
          <Snackbar 
            open={!!successMessage} 
            autoHideDuration={6000} 
            onClose={() => setSuccessMessage(null)}
          >
            <StyledAlert severity="success">
              {successMessage}
            </StyledAlert>
          </Snackbar>

          {/* Service Selection Modal */}
          <ServiceModal
            open={serviceModalOpen}
            onClose={() => setServiceModalOpen(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={serviceModalOpen}>
              <ModalContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                    SELECT YOUR SERVICE
                  </Typography>
                  <IconButton 
                    onClick={() => setServiceModalOpen(false)}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Popular Services */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 'bold' }}>
                    Popular Services
                  </Typography>
                  <Grid container spacing={2}>
                    {AUTOMOTIVE_SERVICES.filter(service => service.category === 'popular').map((service) => {
                      const IconComponent = service.icon;
                      const isSelected = selectedServices.includes(service.id);
                      return (
                        <Grid size={{ xs: 12, sm: 6 }} key={service.id}>
                          <ServiceCard
                            className={isSelected ? 'selected' : ''}
                            onClick={() => handleServiceToggle(service.id)}
                          >
                            <IconComponent sx={{ color: isSelected ? '#3a86ff' : 'rgba(255, 255, 255, 0.7)', fontSize: 28 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                {service.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {service.description}
                              </Typography>
                            </Box>
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                '&.Mui-checked': {
                                  color: '#3a86ff',
                                },
                              }}
                            />
                          </ServiceCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 4 }} />

                {/* Custom Services */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 'bold' }}>
                    Select your own service
                  </Typography>
                  <Grid container spacing={2}>
                    {AUTOMOTIVE_SERVICES.filter(service => service.category === 'custom').map((service) => {
                      const IconComponent = service.icon;
                      const isSelected = selectedServices.includes(service.id);
                      return (
                        <Grid size={{ xs: 12, sm: 6 }} key={service.id}>
                          <ServiceCard
                            className={isSelected ? 'selected' : ''}
                            onClick={() => handleServiceToggle(service.id)}
                          >
                            <IconComponent sx={{ color: isSelected ? '#3a86ff' : 'rgba(255, 255, 255, 0.7)', fontSize: 28 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                {service.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {service.description}
                              </Typography>
                            </Box>
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                '&.Mui-checked': {
                                  color: '#3a86ff',
                                },
                              }}
                            />
                          </ServiceCard>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Custom Issue Text Field */}
                  {selectedServices.includes('custom-issue') && (
                    <Box sx={{ mt: 2 }}>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Please describe the issue you're experiencing..."
                        value={customIssueText}
                        onChange={(e) => setCustomIssueText(e.target.value)}
                      />
                    </Box>
                  )}

                  {/* Specific Part Field */}
                  {selectedServices.includes('specific-part') && (
                    <Box sx={{ mt: 2 }}>
                      <StyledTextField
                        fullWidth
                        placeholder="Enter the specific part (e.g., brake pads, air filter, etc.)"
                        value={selectedPart}
                        onChange={(e) => setSelectedPart(e.target.value)}
                      />
                    </Box>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setServiceModalOpen(false)}
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <GradientButton
                    onClick={handleServiceModalClose}
                    disabled={selectedServices.length === 0}
                  >
                    NEXT
                  </GradientButton>
                </Box>
              </ModalContent>
            </Fade>
          </ServiceModal>
        </motion.div>
      </Container>
    </GradientBackground>
  );
};

export default BookAppointmentPage; 